import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Send, User, Bot } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatPage() {
  const [step, setStep] = useState("welcome");
  const [customerData, setCustomerData] = useState({ name: "", email: "" });
  const [customerId, setCustomerId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    if (!customerData.name.trim() || !customerData.email.trim()) {
      toast.error("الرجاء إدخال الاسم والبريد الإلكتروني");
      return;
    }

    try {
      const response = await axios.post(`${API}/customers`, customerData);
      setCustomerId(response.data.id);
      setStep("chat");

      // Load chat history
      const history = await axios.get(`${API}/chat/${response.data.id}`);
      if (history.data.length > 0) {
        const formattedHistory = [];
        history.data.forEach(msg => {
          formattedHistory.push({ text: msg.message, sender: "user" });
          formattedHistory.push({ text: msg.response, sender: "bot" });
        });
        setMessages(formattedHistory);
      } else {
        setMessages([{
          text: "مرحباً بك! أنا المساعد الذكي لشركة إسكندر سوفت. كيف يمكنني مساعدتك اليوم؟",
          sender: "bot"
        }]);
      }
    } catch (error) {
      console.error("Error creating customer:", error);
      toast.error("حدث خطأ. الرجاء المحاولة مرة أخرى");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setMessages(prev => [...prev, { text: userMessage, sender: "user" }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        customer_id: customerId,
        message: userMessage
      });

      setMessages(prev => [...prev, { text: response.data.response, sender: "bot" }]);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("حدث خطأ في إرسال الرسالة");
      setMessages(prev => [...prev, {
        text: "عذراً، حدث خطأ في معالجة رسالتك. الرجاء المحاولة مرة أخرى.",
        sender: "bot"
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-50 to-cyan-50">
      {step === "welcome" ? (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="w-full max-w-md">
            <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-blue-100">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-500 rounded-2xl mb-4 shadow-lg">
                  <MessageCircle className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">مرحباً بك</h1>
                <p className="text-gray-600">المساعد الذكي لشركة إسكندر سوفت</p>
              </div>

              <form onSubmit={handleCustomerSubmit} className="space-y-6" data-testid="customer-info-form">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700 font-medium">الاسم</Label>
                  <Input
                    id="name"
                    data-testid="customer-name-input"
                    type="text"
                    placeholder="أدخل اسمك"
                    value={customerData.name}
                    onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                    className="h-12 rounded-xl border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                    dir="rtl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">البريد الإلكتروني</Label>
                  <Input
                    id="email"
                    data-testid="customer-email-input"
                    type="email"
                    placeholder="example@email.com"
                    value={customerData.email}
                    onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                    className="h-12 rounded-xl border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="start-chat-button"
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white rounded-xl font-medium shadow-lg"
                >
                  بدء المحادثة
                </Button>
              </form>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col h-screen" data-testid="chat-interface">
          <div className="bg-white/80 backdrop-blur-md border-b border-blue-100 px-6 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-500 rounded-xl flex items-center justify-center shadow-md">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">إسكندر سوفت</h2>
                <p className="text-sm text-gray-600">المساعد الذكي</p>
              </div>
            </div>
          </div>

          <ScrollArea className="flex-1 p-6">
            <div className="max-w-4xl mx-auto space-y-4" data-testid="messages-container">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  data-testid={`message-${msg.sender}-${idx}`}
                  className={`flex gap-3 ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.sender === "bot" && (
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] px-4 py-3 rounded-2xl shadow-sm ${
                      msg.sender === "user"
                        ? "bg-gradient-to-r from-blue-500 to-blue-500 text-white rounded-tr-sm"
                        : "bg-white border border-blue-100 text-gray-800 rounded-tl-sm"
                    }`}
                    dir="rtl"
                  >
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                  </div>
                  {msg.sender === "user" && (
                    <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-gray-600" />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-3 justify-start" data-testid="loading-indicator">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-white border border-blue-100 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          <div className="bg-white/80 backdrop-blur-md border-t border-blue-100 p-4 shadow-lg">
            <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
              <div className="flex gap-3">
                <Input
                  data-testid="message-input"
                  type="text"
                  placeholder="اكتب رسالتك هنا..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  disabled={isLoading}
                  className="flex-1 h-12 rounded-xl border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                  dir="rtl"
                />
                <Button
                  type="submit"
                  data-testid="send-message-button"
                  disabled={isLoading || !inputMessage.trim()}
                  className="h-12 px-6 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white rounded-xl shadow-md"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}